/**
 * Maps a Stripe payment error (from `stripe.confirmPayment`) to a **generic, localised**
 * i18n key. We never surface the raw English Stripe message to the patient (PAY-E1 / US 1.3):
 * the raw message can leak card details, is not translated, and is not friendly.
 *
 * Written from scratch for Kalienda (no `packages/features/ee/payments` code copied).
 * Only the public Stripe error taxonomy (`error.code` / `error.decline_code`) is used.
 */

/** Minimal shape of a Stripe.js error we rely on. Kept structural to avoid a hard SDK type import. */
export interface StripeLikeError {
  type?: string;
  code?: string;
  decline_code?: string;
}

/** All i18n keys this module can return. Each must exist in en/fr/nl common.json. */
export const STRIPE_PAYMENT_ERROR_KEYS = [
  "payment_error_card_declined",
  "payment_error_insufficient_funds",
  "payment_error_expired_card",
  "payment_error_incorrect_number",
  "payment_error_incorrect_cvc",
  "payment_error_processing",
  "payment_error_authentication",
  "payment_error_generic",
] as const;

export type StripePaymentErrorKey = (typeof STRIPE_PAYMENT_ERROR_KEYS)[number];

const DECLINE_CODE_KEYS: Record<string, StripePaymentErrorKey> = {
  insufficient_funds: "payment_error_insufficient_funds",
  expired_card: "payment_error_expired_card",
  incorrect_cvc: "payment_error_incorrect_cvc",
  invalid_cvc: "payment_error_incorrect_cvc",
  incorrect_number: "payment_error_incorrect_number",
  invalid_number: "payment_error_incorrect_number",
};

const CODE_KEYS: Record<string, StripePaymentErrorKey> = {
  card_declined: "payment_error_card_declined",
  expired_card: "payment_error_expired_card",
  incorrect_cvc: "payment_error_incorrect_cvc",
  invalid_cvc: "payment_error_incorrect_cvc",
  incorrect_number: "payment_error_incorrect_number",
  invalid_number: "payment_error_incorrect_number",
  processing_error: "payment_error_processing",
  payment_intent_authentication_failure: "payment_error_authentication",
};

/**
 * Returns the generic i18n key for a Stripe error. `decline_code` refines a `card_declined`
 * (e.g. insufficient funds) and always wins when recognised. Unknown errors fall back to the
 * generic key. Never returns the raw Stripe message.
 */
export function getStripePaymentErrorKey(error: StripeLikeError | null | undefined): StripePaymentErrorKey {
  if (!error) {
    return "payment_error_generic";
  }

  if (error.decline_code && DECLINE_CODE_KEYS[error.decline_code]) {
    return DECLINE_CODE_KEYS[error.decline_code];
  }

  if (error.code && CODE_KEYS[error.code]) {
    return CODE_KEYS[error.code];
  }

  return "payment_error_generic";
}
